import React, { useEffect, useState } from "react";
import {
  IconDownload,
  IconPlus,
  IconEdit,
  IconEye,
  IconSearch,
  IconX,
} from "@tabler/icons-react";
import MessageModal from "../../components/MessageModal";
import ColumnsFilter from "../../components/common/ColumnsFilter";
import DropdownFilter from "../../components/common/DropdownFilter";
import ListView from "../../components/common/ListView";
import ToggleButton from "../../components/ToggleButton";
import AddNewOutlet from "./_components/AddNewOutlet";
import EditNewmodel from "./_components/EditNewmodel";
import {
  useFetchOutlets,
  useUpdateOutletStatusMutation,
} from "../../apis/outletApis";
import toast from "react-hot-toast";
import formatDate from "../../utilities/helper";
import Pagination from "../../components/common/Pagination";
import useDebounce from "../../utilities/useDebounce";
import EntriesSelector from "../../components/EntriesSelector";
import NormalSearch from "../../components/common/NormalSearch";
import OutletPreviewModal from "./_components/OutletPreviewModal";
import { usePermission } from "../../hooks/usePermissionTabs";
import { useLanguage } from "../../context/LanguageContext";

type Column<T> = {
  label: string;
  render: (row: T, index: number) => React.ReactNode;
};

const OutletManagement: React.FC = () => {
  const { t } = useLanguage();
  const { allowed: canReadOutlet, isLoading: isPermissionLoading } =
    usePermission("outlet", "read");
  const { allowed: canCreateOutlet } = usePermission("outlet", "create");
  const { allowed: canUpdateOutlet } = usePermission("outlet", "update");

  const [searchFor, setSearchFor] = useState<string>("");
  const [showSearch, setShowSearch] = useState<boolean>(false);
  const [pageNo, setPageNo] = useState<number>(1);
  const [limit, setLimit] = useState<number>(10);
  const debouncedSearchFor = useDebounce(searchFor, 550);
  const [openPreviewModal, setOpenPreviewModal] = useState<boolean>(false);

  const { data, isLoading, refetch } = useFetchOutlets(
    pageNo,
    limit,
    debouncedSearchFor,
  );

  const totalPages = data?.total ? Math.ceil(data.total / limit) : 1;

  useEffect(() => {
    refetch();
  }, [debouncedSearchFor, pageNo, limit, refetch]);

  const { mutate: updateStatus, isPending: isStatusPending } =
    useUpdateOutletStatusMutation();

  const [openAddModal, setOpenAddModal] = useState<boolean>(false);
  const [openEditModal, setOpenEditModal] = useState<boolean>(false);
  const [statusModalVisible, setStatusModal] = useState<boolean>(false);

  const [selectedOutlet, setSelectedOutlet] = useState<any>(null);

  const handleToggleStatus = (outlet: any) => {
    if (!canUpdateOutlet) {
      toast.error(t("You do not have permission to update outlet status"));
      return;
    }
    setSelectedOutlet(outlet);
    setStatusModal(true);
  };

  const handleConfirmStatusChange = () => {
    if (!selectedOutlet) return;
    const newStatus =
      selectedOutlet?.status === "ACTIVE" ? "INACTIVE" : "ACTIVE";
    updateStatus(
      { id: selectedOutlet?.id, status: newStatus },
      {
        onSuccess: () => {
          toast.success(t("Status updated successfully"));
          setStatusModal(false);
          setSelectedOutlet(null);
          refetch();
        },
        onError: (error: any) => {
          toast.error(
            t(error?.response?.data?.message || "Failed to update status"),
          );
        },
      },
    );
  };

  const handleOpenEdit = (outlet: any) => {
    setOpenAddModal(false);
    setOpenPreviewModal(false);
    setSelectedOutlet(outlet);
    setOpenEditModal(true);
  };

  const handlePaginationFetch = (page: number) => {
    setPageNo(page);
  };

  const [columnsData, setColumnsData] = useState([
    { text: "S/N", value: "sn", isShow: true },
    { text: "Outlet Name", value: "name", isShow: true },
    { text: "Code", value: "code", isShow: true },
    { text: "Email", value: "email", isShow: true },
    { text: "Short Address", value: "shortAddress", isShow: true },
    { text: "Mobile", value: "mobile", isShow: true },
    { text: "City", value: "city", isShow: true },
    { text: "Create Date", value: "createDate", isShow: true },
    { text: "Status", value: "status", isShow: true },
    { text: "Actions", value: "actions", isShow: true },
  ]);

  const setHeadings = (value: string | undefined, checked: boolean) => {
    setColumnsData((prev) =>
      prev.map((col) =>
        col.value === value ? { ...col, isShow: checked } : col,
      ),
    );
  };

  const showRightPanel =
    openAddModal ||
    (openEditModal && !!selectedOutlet) ||
    (openPreviewModal && !!selectedOutlet);

  // Columns remain visible when the right panel is open to allow horizontal scrolling

  const columns: Column<(typeof data.data)[number]>[] = columnsData
    .filter((c) => c.isShow)
    .map((col) => {
      switch (col.value) {
        case "sn":
          return {
            label: t("S/N"),
            render: (_row, i) => (pageNo - 1) * limit + i + 1,
          };
        case "name":
          return {
            label: t("Outlet Name"),
            render: (row) => row.outletName,
          };
        case "code":
          return {
            label: t("Code"),
            render: (row) => row.code,
          };
        case "email":
          return {
            label: t("Email"),
            render: (row) => row.email,
          };
        case "shortAddress":
          return {
            label: t("Short Address"),
            render: (row) => row.shortAddress,
          };
        case "mobile":
          return {
            label: t("Mobile"),
            render: (row) => row.mobileNo,
          };
        case "city":
          return {
            label: t("City"),
            render: (row) => row.cityName,
          };
        case "createDate":
          return {
            label: t("Create Date"),
            render: (row) => formatDate(row.createdAt),
          };
        case "status":
          return {
            label: t("Status"),
            render: (row) => (
              <div
                className="flex justify-center"
                onClick={(e) => e.stopPropagation()}
              >
                <ToggleButton
                  onChange={() => handleToggleStatus(row)}
                  checked={row.status === "ACTIVE"}
                />
              </div>
            ),
          };
        case "actions":
          return {
            label: t("Actions"),
            render: (row) => (
              <div className="flex justify-center gap-2">
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setOpenAddModal(false);
                    setOpenEditModal(false);
                    setSelectedOutlet(row);
                    setOpenPreviewModal(true);
                  }}
                  className="p-2 border rounded-full bg-[#F7F6F2] hover:bg-yellow-100 transition"
                  title={t("Preview")}
                >
                  <IconEye size={22} />
                </button>
              </div>
            ),
          };
        default:
          return {
            label: col.text,
            render: (row) => row[col.value as keyof typeof row] ?? "—",
          };
      }
    }) as Column<(typeof data.data)[number]>[];

  if (isPermissionLoading) {
    return (
      <div className="px-6 pb-8 mt-4 w-full">
        <div className="bg-white rounded-3xl p-6 h-64 animate-pulse" />
      </div>
    );
  }

  if (!canReadOutlet) {
    return (
      <div className="px-6 pb-8 mt-4 w-full">
        <div className="bg-white rounded-3xl p-6 text-center text-gray-500">
          {t("No access to any section.")}
        </div>
      </div>
    );
  }

  return (
    <div className="px-4 md:px-6 py-2 space-y-4 pb-8">
      <div className="grid grid-cols-12 gap-6 p-2  bg-gradient-to-b from-white/90 to-white/60 backdrop-blur-xl transition-all duration-300 w-ful rounded-3xl w-full">
        <div className="absolute inset-0 rounded-3xl bg-gradient-to-tr from-yellow-200/20 to-transparent pointer-events-none" />
        <div
          className={
            showRightPanel
              ? "col-span-12 xl:col-span-7 space-y-4 min-w-0"
              : "col-span-12 space-y-4 min-w-0"
          }
        >
          <div className="bg-white rounded-3xl overflow-visible mb-6">
            {/* Header Row (Filters + Actions Unified) */}
            <div className="px-6 py-3.5 flex flex-col lg:flex-row lg:items-center justify-between gap-4">
              {/* Left side: Brand badge */}
              <div className="flex flex-wrap items-center gap-3"></div>

              {/* Right side: Export + Search + Advanced Filters */}
              <div className="flex items-center flex-wrap gap-2.5">
                {/* Sliding Search */}
                <div className="relative flex items-center">
                  <div
                    onClick={() => {
                      if (!showSearch) {
                        setShowSearch(true);
                      }
                    }}
                    className={`flex items-center transition-all duration-300 ease-in-out border border-slate-200 rounded-lg bg-white shadow-sm h-8 ${
                      showSearch
                        ? "w-64 px-2 cursor-default"
                        : "w-8 justify-center cursor-pointer hover:bg-slate-50 hover:shadow-md hover:-translate-y-0.5 active:translate-y-0 active:scale-95"
                    }`}
                  >
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setShowSearch(!showSearch);
                      }}
                      className="flex items-center justify-center text-slate-700 focus:outline-none flex-shrink-0"
                      title={t("Search")}
                    >
                      <IconSearch size={17} stroke={2.5} />
                    </button>
                    {showSearch && (
                      <>
                        <input
                          type="text"
                          value={searchFor}
                          onChange={(e) => {
                            setSearchFor(e.target.value);
                            setPageNo(1);
                          }}
                          placeholder={t("Search...")}
                          autoFocus
                          className="flex-1 ml-2 bg-transparent text-xs text-slate-800 placeholder-slate-400 outline-none border-none"
                        />
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setSearchFor("");
                            setShowSearch(false);
                          }}
                          className="flex items-center justify-center p-0.5 ml-1 text-slate-400 hover:text-slate-600 focus:outline-none flex-shrink-0"
                          title={t("Clear & Close")}
                        >
                          <IconX size={14} stroke={2.5} />
                        </button>
                      </>
                    )}
                  </div>
                </div>

                {/* Export */}
                <button
                  className="w-8 h-8 flex items-center justify-center bg-white hover:bg-slate-50 border border-slate-200 text-slate-700 rounded-lg transition-all duration-150 shadow-sm hover:shadow-md hover:-translate-y-0.5 active:translate-y-0 active:scale-95 cursor-pointer"
                  title={t("Download")}
                >
                  <IconDownload size={17} stroke={2.5} />
                </button>

                {/* Entries limit selector */}
                {data && data.total * limit > 10 && (
                  <EntriesSelector
                    value={limit}
                    options={[
                      10,
                      ...(data.total * limit > 10 ? [20] : []),
                      ...(data.total * limit > 20 ? [30] : []),
                      ...(data.total * limit > 50 ? [50] : []),
                      ...(data.total * limit > 100 ? [100] : []),
                    ]}
                    onChange={(val) => {
                      setLimit(val);
                      setPageNo(1);
                    }}
                  />
                )}

                {/* Columns Filter */}
                <ColumnsFilter
                  columnsData={columnsData}
                  setHeadings={setHeadings}
                />

                {/* Dropdown Filter */}
                <DropdownFilter
                  filterheadings={[]}
                  triggerClassName="border border-slate-200 bg-white rounded-lg cursor-pointer text-[#212121] hover:bg-slate-50 hover:shadow-md hover:-translate-y-0.5 active:translate-y-0 active:scale-95 transition-all duration-150 h-8 w-8 flex items-center justify-center shadow-sm select-none"
                  iconSize={17}
                />

                {/* Add Outlet Button (Placed Last and Color Filled with Yellow) */}
                {canCreateOutlet && (
                  <button
                    onClick={() => {
                      setOpenAddModal(true);
                      setOpenEditModal(false);
                      setOpenPreviewModal(false);
                      setSelectedOutlet(null);
                    }}
                    className="w-8 h-8 flex items-center justify-center rounded-lg transition-all duration-150 border border-[#FBD037] bg-[#FBD037] text-[#131327] hover:bg-[#e2bd30] shadow-sm hover:shadow-md hover:-translate-y-0.5 active:translate-y-0 active:scale-95 cursor-pointer"
                    title={t("Add Outlet")}
                  >
                    <IconPlus size={17} stroke={2.5} />
                  </button>
                )}
              </div>
            </div>

            {/* Table */}
            <div className="border-t border-slate-100 rounded-b-3xl overflow-x-auto w-full">
              {isLoading ? (
                <div className="p-8 text-center text-gray-500">
                  {t("Loading outlets...")}
                </div>
              ) : (
                <ListView
                  data={data?.data || []}
                  columns={columns}
                  mainDivClassName="bg-white rounded-b-3xl overflow-x-auto w-full"
                  mailDivClassName="p-0 rounded-b-3xl overflow-x-auto w-full"
                  scrollbarClassName="custom-scrollbar"
                />
              )}
            </div>
          </div>
        </div>

        {/* ── Sidebar Right Panel ────────────────────────────────────────── */}
        {showRightPanel && (
          <>
            {/* Backdrop on mobile */}
            <div
              className="fixed inset-0 bg-black/40 z-30 xl:hidden"
              onClick={() => {
                setOpenAddModal(false);
                setOpenEditModal(false);
                setOpenPreviewModal(false);
                setSelectedOutlet(null);
              }}
            />
           <div className="col-span-12 xl:col-span-5 space-y-4  mt-2 xl:static fixed inset-y-0 right-0 z-[35] w-full sm:w-[450px] xl:w-auto p-4 pt-[76px] sm:pt-[96px] xl:p-0 shadow-2xl xl:shadow-none bg-white/21 xl:bg-transparent overflow-y-auto xl:overflow-visible flex flex-col justify-start">
              {openAddModal && (
                <AddNewOutlet
                  onSuccess={() => {
                    setOpenAddModal(false);
                    refetch();
                  }}
                  onClose={() => setOpenAddModal(false)}
                  isSidebar
                />
              )}

              {openEditModal && selectedOutlet && (
                <AddNewOutlet
                  outletId={selectedOutlet?.id}
                  outletData={selectedOutlet}
                  onClose={() => {
                    setOpenEditModal(false);
                    setSelectedOutlet(null);
                  }}
                  onSuccess={() => {
                    setOpenEditModal(false);
                    refetch();
                  }}
                  isSidebar
                />
              )}

              {openPreviewModal && selectedOutlet && (
                <OutletPreviewModal
                  outletData={selectedOutlet}
                  onClose={() => {
                    setOpenPreviewModal(false);
                    setSelectedOutlet(null);
                  }}
                  onEdit={
                    canUpdateOutlet
                      ? () => handleOpenEdit(selectedOutlet)
                      : undefined
                  }
                  mode="view"
                  isSidebar
                />
              )}
            </div>
          </>
        )}

        <div className="flex justify-end items-end px-4 col-span-12"> 
          {totalPages > 1 && (
            <Pagination
              currentPage={pageNo}
              totalPages={totalPages}
              fetchData={handlePaginationFetch}
            />
          )}
        </div>

        <MessageModal
          open={statusModalVisible}
          onClose={() => setStatusModal(false)}
          title={t("Confirmation Required")}
          subtitle={
            selectedOutlet?.status === "ACTIVE"
              ? t("Are you sure you want to deactivate this outlet?")
              : t("Are you sure you want to activate this outlet?")
          }
          img={
            selectedOutlet?.status === "ACTIVE"
              ? "/img/inactive.svg"
              : "/img/active.svg"
          }
          showFooter={true}
          closeButtonText={t("Cancel")}
          actionButtonText={t("Confirm")}
          actionButtonOnClick={handleConfirmStatusChange}
          isPending={isStatusPending}
          size="md"
        />
      </div>
    </div>
  );
};

export default OutletManagement;
